/**
 * The day solver: greedy assignment plus bounded backtracking and local search.
 *
 * Placing lunches optimally is a bin-packing problem with side constraints, so
 * there is no exact algorithm that stays inside a browser frame on a forty-person
 * rota. What managers actually want is not optimality anyway - it is a schedule
 * that is *defensible*. So the search is deliberately explainable:
 *
 *   1. Locked assignments seed the state and are never touched again.
 *   2. Everyone else is sorted by (group, preferred time, shift start, name), so
 *      earlier starters get first pick - the rule the manual schedules follow.
 *   3. A weighted greedy pass gives each person their best feasible slot.
 *   4. Anyone who cannot be placed triggers bounded backtracking: displace one
 *      already-placed, unlocked colleague into their own next-best slot, but only
 *      when the combined score improves and both stay feasible.
 *   5. `settings.improvementPasses` sweeps of local search - single moves and
 *      pairwise swaps - keeping only strict improvements.
 *   6. Anyone still unplaced is retried with the soft rules relaxed, if the
 *      manager has allowed that; otherwise they come back as an
 *      `UnassignedRecord` naming the rules that stopped them.
 *
 * **Relaxation never touches a hard rule.** Only the group lunch window and the
 * preferred spacing may be given up. Staying inside the shift, the minimum staff
 * on duty, the cap on simultaneous lunches and one-lunch-per-person are the
 * promises the app is for, and a schedule that quietly breaks one of them is
 * worse than no schedule at all.
 *
 * **The solver is deterministic.** No `Math.random`, no `Date.now`, no iteration
 * over unordered keys without an explicit sort. The same context in gives the
 * same schedule out, every time - otherwise "regenerate" becomes a slot machine
 * and nobody trusts the output.
 *
 * The `draft` objects the search shuffles around are created here and never
 * escape: the Assignments returned at the end are freshly built. Nothing handed
 * in is mutated.
 */

import { DEFAULT_SETTINGS, LUNCH_GROUPS, resolveDayConfig } from '../core/settings.js';
import { ASSIGNMENT_SOURCES, createAssignment } from '../core/model.js';
import { DAY_LABELS, formatDuration, formatMinutes } from '../core/time.js';
import { INELIGIBLE } from './eligibility.js';
import { buildCandidates, scoreCandidate } from './scoring.js';
import { explainAssignment } from './explain.js';

/**
 * How many already-placed people one blocked entry may push out of the way.
 * Two is enough to resolve the ordinary "both of us want 13:00 and the person
 * at 13:30 could just as easily eat at 14:00" chain, and small enough that the
 * worst case stays trivial. Deeper searches buy almost nothing on real rotas
 * and make the outcome much harder to explain.
 */
const MAX_BACKTRACK_DEPTH = 2;

/**
 * Hard ceiling on full-solution evaluations during local search. A pathological
 * rota (fifty people, one legal slot each) must degrade to "good enough" rather
 * than lock up the browser tab.
 */
const MAX_SEARCH_EVALUATIONS = 20000;

/**
 * Work ceiling for the whole search, expressed as evaluations × solution size².
 *
 * One `evaluateSolution()` costs roughly O(placed²): it re-scores every draft,
 * and scoring a draft walks the other lunches to work out coverage. Capping the
 * *count* of evaluations therefore still lets total work grow cubically with the
 * rota. Measured on this machine, a flat 20 000-evaluation cap costs 53ms for a
 * 32-person day but 7.9s for a 64-person one and 23s at 200 - all of it on the
 * main thread, with the interface frozen behind it.
 *
 * Budgeting the product instead keeps generation interactive at any size. The
 * ceiling is set from the measured throughput (~4.5M scoring operations per
 * second) to keep a single day comfortably inside a third of a second, which
 * leaves a seven-day generate feeling instant. Real rotas - this team runs 32
 * staff, of whom about 15 are eligible on a given day - never come close to it,
 * so nothing regresses in practice; only pathological inputs degrade, and they
 * degrade to "good enough, quickly" rather than to a hung tab.
 */
const MAX_SEARCH_WORK = 1_400_000;

/** Never search less than this, however large the rota. */
const MIN_SEARCH_EVALUATIONS = 50;

/** Evaluations affordable for a day holding `size` lunches. */
function affordableEvaluations(size) {
  const n = Math.max(1, Number(size) || 1);
  return Math.max(MIN_SEARCH_EVALUATIONS, Math.floor(MAX_SEARCH_WORK / (n * n)));
}

/** Improvements smaller than this are floating-point noise, not improvements. */
const IMPROVEMENT_EPSILON = 1e-6;

/**
 * What one empty half-hour inside a lunch run costs.
 *
 * Deliberately small: a tidy, gap-free run is a *cosmetic* preference and must
 * never outrank putting each person's lunch at a sensible point in their own
 * shift. At its original value of 20 it dwarfed every individual term (those are
 * normalised to ±1 and weighted at most 2), so on a thin day the solver
 * compressed everybody into a block starting at 12:00 — which is how a
 * 12:00-20:00 shift ended up eating at 14:00, two hours in.
 *
 * The historical schedules settle the question: the real Friday ran 12:00,
 * 12:30, 13:00, 14:00, 15:00, 16:00, leaving 13:30, 14:30 and 15:30 empty. Gaps
 * on a quiet day are normal practice here, not a defect to be optimised away.
 */
const COMPACTNESS_GAP_PENALTY = 2;

/**
 * What it costs, per slot, to place somebody outside their preferred lunch
 * range. Charged on the square of the distance so a 30-minute nudge to resolve a
 * clash stays cheap while dragging somebody hours from the right point becomes
 * unaffordable — and always dearer than the gap it would have closed.
 */
const MISPLACEMENT_PENALTY_PER_SLOT = 6;

/**
 * Starting a required run late is an operational failure, not an ordinary
 * preference. This anchors daytime at 12:00 and Saturday evening at 18:30.
 * The large lexicographic penalty keeps each anchor ahead of every collection
 * of soft scoring gains while hard safety rules remain absolute.
 */
const RUN_LATE_START_PENALTY = 1000;

/**
 * The two soft rules relaxation may switch off. `scoring.js` reads these same
 * strings off `entry.relaxations`; they are spelled out in both files because
 * neither module may export anything the architecture contract does not list.
 */
const RELAX_WINDOW = 'lunch-window';
const RELAX_SPREAD = 'preferred-spread';

/** Day lunches are settled before evening ones, evening before night. */
const GROUP_ORDER = { day: 0, evening: 1, night: 2 };

/**
 * Solve one day.
 *
 * @param {object} context DayContext - `{ dayKey, date, settings, dayConfig,
 *   shifts, working, entries, rulesIndex, lockedAssignments, fairness }`.
 *   Only `entries` is really required; the rest is derived where it is missing so
 *   that a test can hand over a minimal object.
 * @returns {{ assignments: object[], unassigned: object[], solved: boolean,
 *   relaxed: object[], scoreTotal: number }}
 */
export function solveDay(context) {
  const settings = context?.settings ?? DEFAULT_SETTINGS;
  const dayKey = context?.dayKey ?? context?.entries?.[0]?.dayKey ?? '';
  const dayConfig = context?.dayConfig ?? resolveDayConfig(settings, dayKey);
  const lunchMinutes = positive(settings.lunchDurationMinutes, DEFAULT_SETTINGS.lunchDurationMinutes);
  const slotInterval = positive(settings.slotIntervalMinutes, DEFAULT_SETTINGS.slotIntervalMinutes);
  const allEntries = Array.isArray(context?.entries) ? context.entries : [];

  const ctx = {
    ...context,
    dayKey,
    settings,
    dayConfig,
    entries: allEntries,
    working: Array.isArray(context?.working)
      ? context.working
      : (context?.shifts ?? []).filter((shift) => shift?.kind === 'shift'),
  };

  /* --- Solver state ----------------------------------------------------- */

  const state = {
    /** slotMinutes -> drafts occupying it. Kept in step with `assigned`. */
    occupancy: new Map(),
    /** Every draft (and locked stand-in) currently placed. */
    assigned: [],
    /** Head count per instant. Safe to cache: it depends only on the rota. */
    coverageCache: new Map(),
    /** Running count of well-placed slots per employee, this week. */
    fairness: seedFairness(context?.fairness),
  };

  /** employeeId -> the draft currently placed for them. */
  const drafts = new Map();
  /** Candidate lists, keyed by entry object so a relaxed copy gets its own. */
  const candidateCache = new Map();
  /** employeeId -> the entry the search is using (relaxed copy, or the original). */
  const searchEntries = new Map();

  let budget = MAX_SEARCH_EVALUATIONS;

  /* --- 1. Seed from locks ----------------------------------------------- */

  const lockedAssignments = readLocked(context);
  const lockedIds = new Set();
  for (const locked of lockedAssignments) {
    lockedIds.add(locked.employeeId);
    // A stand-in copy so the stored assignment is never written through.
    addToState(makeDraft(entryFor(locked.employeeId), locked.slotMinutes, locked.employeeId, locked.name));
  }

  /* --- 2. Order the queue ----------------------------------------------- */

  const queue = allEntries
    .filter((entry) => entry?.eligible && !lockedIds.has(entry.employeeId))
    .slice()
    .sort(compareEntries);

  const entryById = new Map();
  for (const entry of queue) {
    entryById.set(entry.employeeId, entry);
    searchEntries.set(entry.employeeId, entry);
  }

  /* --- 3 & 4. Greedy pass with bounded backtracking ---------------------- */

  // Set the work budget now the problem size is known. Backtracking spends the
  // same evaluations the local search does, so it has to share the same ceiling
  // - budgeting only the polish phase left a 64-person rota taking eight
  // seconds in the placement loop alone.
  budget = Math.min(budget, affordableEvaluations(queue.length + lockedIds.size));

  const unplaced = [];
  for (const entry of queue) {
    if (!attemptPlacement(entry, MAX_BACKTRACK_DEPTH)) unplaced.push(entry);
  }

  /* --- 5. Local search --------------------------------------------------- */

  runLocalSearch();

  /* --- 6. Soft-rule relaxation ------------------------------------------- */

  const relaxed = [];
  const stillUnplaced = [];
  for (const entry of unplaced) {
    const placedByRelaxing = settings.allowSoftRuleRelaxation === true
      ? relaxAndPlace(entry, relaxed)
      : false;
    if (!placedByRelaxing) stillUnplaced.push(entry);
  }

  /* --- Results ----------------------------------------------------------- */

  const assignments = [
    ...lockedAssignments,
    ...buildAssignments(),
  ].sort(compareAssignments);

  const unassigned = stillUnplaced.map((entry) => describeFailure(entry));
  const final = evaluateSolution();

  return {
    assignments,
    unassigned,
    solved: unassigned.length === 0,
    relaxed,
    scoreTotal: Number.isFinite(final.total) ? Math.round(final.total * 1000) / 1000 : 0,
  };

  /* ======================================================================== */
  /* State bookkeeping                                                        */
  /* ======================================================================== */

  /**
   * Put a draft into the state.
   *
   * Occupancy, the flat list and the fairness counter are updated together so
   * that a candidate scored halfway through a move can never see a half-applied
   * world - every read the scorer makes is against a consistent snapshot.
   */
  function addToState(draft) {
    state.assigned.push(draft);
    const bucket = state.occupancy.get(draft.slotMinutes);
    if (bucket) bucket.push(draft);
    else state.occupancy.set(draft.slotMinutes, [draft]);

    draft.goodSlot = isWellPlaced(draft);
    if (draft.goodSlot) {
      state.fairness.set(draft.employeeId, (state.fairness.get(draft.employeeId) ?? 0) + 1);
    }
  }

  /** Exact inverse of {@link addToState}. */
  function removeFromState(draft) {
    const index = state.assigned.indexOf(draft);
    if (index >= 0) state.assigned.splice(index, 1);

    const bucket = state.occupancy.get(draft.slotMinutes);
    if (bucket) {
      const at = bucket.indexOf(draft);
      if (at >= 0) bucket.splice(at, 1);
      if (bucket.length === 0) state.occupancy.delete(draft.slotMinutes);
    }

    if (draft.goodSlot) {
      const count = (state.fairness.get(draft.employeeId) ?? 0) - 1;
      state.fairness.set(draft.employeeId, Math.max(0, count));
    }
  }

  /** Move a placed draft to another slot, keeping every index in step. */
  function relocate(draft, slotMinutes) {
    removeFromState(draft);
    draft.slotMinutes = slotMinutes;
    addToState(draft);
  }

  function makeDraft(entry, slotMinutes, employeeId, name) {
    return {
      employeeId: employeeId ?? entry?.employeeId ?? '',
      name: name ?? entry?.name ?? '',
      dayKey: entry?.dayKey ?? dayKey,
      group: entry?.group ?? 'day',
      preferredMinutes: entry?.preferredMinutes ?? null,
      preferredRangeStartMinutes: entry?.preferredRangeStartMinutes ?? entry?.preferredMinutes ?? null,
      preferredRangeEndMinutes: entry?.preferredRangeEndMinutes ?? entry?.preferredMinutes ?? null,
      slotMinutes,
      score: null,
      goodSlot: false,
    };
  }

  /**
   * "Well placed" = inside the slot range this person wanted. The fairness term in
   * `scoring.js` uses the same test, and the two must agree or the running count
   * stops meaning what the term thinks it means.
   */
  function isWellPlaced(draft) {
    const start = Number(draft.preferredRangeStartMinutes);
    const end = Number(draft.preferredRangeEndMinutes);
    return Number.isFinite(start)
      && Number.isFinite(end)
      && draft.slotMinutes >= start
      && draft.slotMinutes <= end;
  }

  function entryFor(employeeId) {
    return allEntries.find((entry) => entry?.employeeId === employeeId) ?? null;
  }

  /* ======================================================================== */
  /* Scoring                                                                  */
  /* ======================================================================== */

  function candidatesFor(entry) {
    let list = candidateCache.get(entry);
    if (!list) {
      list = buildCandidates(entry, ctx);
      candidateCache.set(entry, list);
    }
    return list;
  }

  function candidateAt(entry, slotMinutes) {
    return candidatesFor(entry).find((item) => item.slotMinutes === slotMinutes) ?? null;
  }

  /**
   * Score every candidate for an entry as if that entry were not placed.
   *
   * Detaching first is what makes a *move* comparable with the placement it
   * would replace: leave the draft in and the person shows up as their own
   * competition for the slot they already hold.
   */
  function scoreAllDetached(entry) {
    const draft = drafts.get(entry.employeeId);
    const wasPlaced = Boolean(draft) && state.assigned.includes(draft);
    if (wasPlaced) removeFromState(draft);
    const scored = candidatesFor(entry).map((candidate) => ({
      candidate,
      breakdown: scoreCandidate(entry, candidate, ctx, state),
    }));
    if (wasPlaced) addToState(draft);
    return scored;
  }

  /** One slot, scored with this entry detached. */
  function scoreDetachedAt(entry, slotMinutes) {
    const candidate = candidateAt(entry, slotMinutes);
    if (!candidate) return null;
    const draft = drafts.get(entry.employeeId);
    const wasPlaced = Boolean(draft) && state.assigned.includes(draft);
    if (wasPlaced) removeFromState(draft);
    const breakdown = scoreCandidate(entry, candidate, ctx, state);
    if (wasPlaced) addToState(draft);
    return breakdown;
  }

  /**
   * The objective the local search maximises.
   *
   * Leave-one-out rather than a running tally: moving one person changes what
   * every colleague sharing a slot is worth, so the only trustworthy total is
   * the one recomputed from the finished arrangement. `feasible` is reported
   * separately because a configuration that breaks a hard rule has no score at
   * all - it must be rejected outright, not merely outscored.
   */
  function evaluateSolution() {
    budget -= 1;
    let total = 0;
    let feasible = true;
    for (const employeeId of movableIds()) {
      const entry = searchEntries.get(employeeId);
      const draft = drafts.get(employeeId);
      if (!entry || !draft) continue;
      const breakdown = scoreDetachedAt(entry, draft.slotMinutes);
      if (!breakdown || !breakdown.feasible) {
        feasible = false;
        continue;
      }
      total += breakdown.total;
    }
    // Score the shape once for the whole day. A per-person term cannot see a
    // hole when both slots around it are shared: detaching either person leaves
    // the same occupied slot behind. Counting unique occupied times here makes
    // every avoidable gap visible, including Sunday and post-13:00 shared runs.
    total -= emptySlotsInLunchRuns() * COMPACTNESS_GAP_PENALTY;
    total -= lateRequiredRunStartSlots() * RUN_LATE_START_PENALTY;
    total -= misplacementPenalty();
    return { total, feasible };
  }

  /**
   * What the current shape costs in lunches placed at the wrong point of
   * somebody's own shift.
   *
   * Scored for the whole day alongside compactness, and for the same reason: the
   * two objectives trade directly against each other, so they have to be
   * comparable on one scale. Distance is measured from the nearest edge of the
   * person's preferred *range* — anywhere inside it is free — and charged on the
   * square of the drift, so closing a gap can justify nudging somebody one slot
   * but never dragging them across the afternoon.
   */
  function misplacementPenalty() {
    let penalty = 0;
    for (const draft of state.assigned) {
      if (!Number.isFinite(draft?.slotMinutes)) continue;
      const from = Number.isFinite(draft.preferredRangeStartMinutes)
        ? draft.preferredRangeStartMinutes
        : draft.preferredMinutes;
      const to = Number.isFinite(draft.preferredRangeEndMinutes)
        ? draft.preferredRangeEndMinutes
        : draft.preferredMinutes;
      if (!Number.isFinite(from) || !Number.isFinite(to)) continue;

      const drift = draft.slotMinutes < from
        ? from - draft.slotMinutes
        : Math.max(0, draft.slotMinutes - to);
      if (drift <= 0) continue;

      const slotsOff = drift / slotInterval;
      penalty += MISPLACEMENT_PENALTY_PER_SLOT * slotsOff * slotsOff;
    }
    return penalty;
  }

  /** Total grid slots by which required daytime/Saturday-evening runs start late. */
  function lateRequiredRunStartSlots() {
    let total = 0;
    for (const group of [LUNCH_GROUPS.DAY, LUNCH_GROUPS.EVENING]) {
      const anchor = requiredRunStart(group);
      if (!Number.isFinite(anchor)) continue;
      const starts = state.assigned
        .filter((draft) => (draft.group ?? LUNCH_GROUPS.DAY) === group)
        .map((draft) => draft.slotMinutes)
        .filter(Number.isFinite);
      if (starts.length === 0) continue;
      total += Math.max(0, Math.round((Math.min(...starts) - anchor) / slotInterval));
    }
    return total;
  }

  /** Empty grid slots between the first and last occupied time in each group. */
  function emptySlotsInLunchRuns() {
    const byGroup = new Map();
    for (const draft of state.assigned) {
      if (!Number.isFinite(draft?.slotMinutes)) continue;
      const group = draft.group ?? 'day';
      const occupied = byGroup.get(group) ?? new Set();
      occupied.add(draft.slotMinutes);
      byGroup.set(group, occupied);
    }

    let gaps = 0;
    for (const occupied of byGroup.values()) {
      const slots = [...occupied].sort((a, b) => a - b);
      for (let index = 1; index < slots.length; index += 1) {
        gaps += Math.max(0, Math.round((slots[index] - slots[index - 1]) / slotInterval) - 1);
      }
    }
    return gaps;
  }

  /** Placed, unlocked employees, in a fixed order so sweeps never vary. */
  function movableIds() {
    return [...drafts.keys()].filter((id) => entryById.has(id)).sort(compareIds);
  }

  /* ======================================================================== */
  /* 3 & 4. Greedy placement and backtracking                                 */
  /* ======================================================================== */

  /** The highest-scoring feasible slot right now, or null if there is none. */
  function bestFeasible(entry) {
    const anchor = feasibleRequiredRunAnchor(entry);
    if (anchor) return anchor;

    let best = null;
    for (const candidate of candidatesFor(entry)) {
      const breakdown = scoreCandidate(entry, candidate, ctx, state);
      if (!breakdown.feasible) continue;
      const better = !best
        || breakdown.total > best.breakdown.total
        // Ties go to the earlier slot: deterministic, and it keeps the day's
        // lunches front-loaded rather than drifting towards the window's end.
        || (breakdown.total === best.breakdown.total
          && candidate.slotMinutes < best.candidate.slotMinutes);
      if (better) best = { candidate, breakdown };
    }
    return best;
  }

  /**
   * Take the first safe opportunity to establish a required group anchor. If
   * this employee cannot use it, ordinary placement continues and somebody
   * later in the queue may still establish it. We never override a hard blocker.
   */
  function feasibleRequiredRunAnchor(entry) {
    const anchorMinutes = requiredRunStart(entry?.group);
    if (!Number.isFinite(anchorMinutes)) return null;
    const alreadyAnchored = state.assigned.some(
      (draft) => (draft.group ?? LUNCH_GROUPS.DAY) === entry.group
        && draft.slotMinutes === anchorMinutes,
    );
    if (alreadyAnchored) return null;

    const candidate = candidateAt(entry, anchorMinutes);
    if (!candidate || candidate.hardViolations.length > 0) return null;
    const breakdown = scoreCandidate(entry, candidate, ctx, state);
    return breakdown.feasible ? { candidate, breakdown } : null;
  }

  /** Daytime always starts at 12:00; Saturday evening additionally starts at 18:30. */
  function requiredRunStart(group) {
    if (group === LUNCH_GROUPS.DAY) return dayConfig.dayWindow.start;
    if (dayKey === 'sat' && group === LUNCH_GROUPS.EVENING) {
      return dayConfig.eveningWindow.start;
    }
    return null;
  }

  function place(entry, slotMinutes, breakdown) {
    const draft = makeDraft(entry, slotMinutes);
    draft.score = Number.isFinite(breakdown?.total) ? breakdown.total : null;
    drafts.set(entry.employeeId, draft);
    addToState(draft);
    return draft;
  }

  function unplace(employeeId) {
    const draft = drafts.get(employeeId);
    if (!draft) return null;
    removeFromState(draft);
    drafts.delete(employeeId);
    return draft;
  }

  function reinstate(draft) {
    drafts.set(draft.employeeId, draft);
    addToState(draft);
  }

  /**
   * Capture and restore the complete movable state around one displacement
   * branch. A depth-two branch may move more than just the immediate victim;
   * restoring only that victim leaves the recursively displaced colleague in
   * the trial position when the outer branch is rejected.
   */
  function snapshotDrafts() {
    return [...drafts.values()].map((draft) => ({
      draft,
      slotMinutes: draft.slotMinutes,
      score: draft.score,
    }));
  }

  function restoreDrafts(snapshot) {
    for (const draft of [...drafts.values()]) removeFromState(draft);
    drafts.clear();
    for (const saved of snapshot) {
      saved.draft.slotMinutes = saved.slotMinutes;
      saved.draft.score = saved.score;
      reinstate(saved.draft);
    }
  }

  /** Greedy first, then - if there is depth left - try to make room. */
  function attemptPlacement(entry, depth) {
    const best = bestFeasible(entry);
    if (best) {
      place(entry, best.candidate.slotMinutes, best.breakdown);
      return true;
    }
    return depth > 0 ? tryDisplace(entry, depth) : false;
  }

  /**
   * Bounded backtracking.
   *
   * Walks the slots this entry could legally use, nearest to its ideal first,
   * and for each one asks whether moving a single unlocked occupant elsewhere
   * would unblock it. The swap is kept only when the whole day scores better
   * *and* everybody involved is still feasible; anything else is rolled back
   * exactly. Recursion is capped by `depth`, so the displaced colleague may
   * displace at most one more person and no further.
   */
  function tryDisplace(entry, depth) {
    const preferred = Number(entry?.preferredMinutes) || 0;
    const options = candidatesFor(entry)
      .filter((candidate) => candidate.hardViolations.length === 0)
      .sort((a, b) => {
        const byDistance = Math.abs(a.slotMinutes - preferred) - Math.abs(b.slotMinutes - preferred);
        return byDistance !== 0 ? byDistance : a.slotMinutes - b.slotMinutes;
      });

    for (const candidate of options) {
      if (budget <= 0) return false;
      for (const victim of occupantsOf(candidate.slotMinutes)) {
        const victimEntry = entryById.get(victim.employeeId);
        if (!victimEntry) continue;

        const snapshot = snapshotDrafts();
        const before = evaluateSolution();
        unplace(victim.employeeId);

        const breakdown = scoreDetachedAt(entry, candidate.slotMinutes);
        if (!breakdown?.feasible) {
          restoreDrafts(snapshot);
          continue;
        }

        place(entry, candidate.slotMinutes, breakdown);
        if (attemptPlacement(victimEntry, depth - 1)) {
          const after = evaluateSolution();
          if (after.feasible && after.total > before.total + IMPROVEMENT_EPSILON) return true;
        }

        restoreDrafts(snapshot);
      }
    }
    return false;
  }

  /**
   * Unlocked lunches overlapping a slot, cheapest first.
   *
   * Overlap rather than equality because the blocker may not be the slot itself:
   * a longer lunch starting half an hour earlier is just as much in the way of
   * the coverage floor.
   */
  function occupantsOf(slotMinutes) {
    return state.assigned
      .filter((draft) => entryById.has(draft.employeeId)
        && draft.slotMinutes < slotMinutes + lunchMinutes
        && slotMinutes < draft.slotMinutes + lunchMinutes)
      .sort((a, b) => {
        const byScore = (a.score ?? 0) - (b.score ?? 0);
        return byScore !== 0 ? byScore : compareIds(a.employeeId, b.employeeId);
      });
  }

  /* ======================================================================== */
  /* 5. Local search                                                          */
  /* ======================================================================== */

  /**
   * Polish the greedy solution with single moves and pairwise swaps.
   *
   * Only strict improvements are kept, so the search can never wander sideways
   * and never oscillates; combined with the evaluation budget that makes the
   * loop provably terminating. A pass that changes nothing ends the search
   * early - there is no value in three identical sweeps.
   */
  function runLocalSearch() {
    const passes = Math.max(0, Math.round(Number(settings.improvementPasses) || 0));
    if (passes === 0 || drafts.size === 0) return;

    // Cap evaluations by what they actually cost, not merely how many there are
    // - see MAX_SEARCH_WORK. Without this a large rota spends tens of seconds
    // polishing, on the main thread, with the UI frozen behind it.
    const size = drafts.size;
    budget = Math.min(
      budget,
      Math.max(MIN_SEARCH_EVALUATIONS, Math.floor(MAX_SEARCH_WORK / (size * size))),
    );

    let current = evaluateSolution();

    for (let pass = 0; pass < passes; pass += 1) {
      if (budget <= 0) break;
      const moved = trySingleMoves(current);
      current = moved.current;
      const swapped = trySwaps(current);
      current = swapped.current;
      const shifted = tryGroupShifts(current);
      current = shifted.current;
      if (!moved.improved && !swapped.improved && !shifted.improved) break;
    }
  }

  /** Try every other legal slot for every movable lunch. */
  function trySingleMoves(startingPoint) {
    let current = startingPoint;
    let improved = false;

    for (const employeeId of movableIds()) {
      if (budget <= 0) break;
      const entry = searchEntries.get(employeeId);
      const draft = drafts.get(employeeId);
      if (!entry || !draft) continue;

      const from = draft.slotMinutes;
      for (const candidate of candidatesFor(entry)) {
        if (budget <= 0) break;
        if (candidate.slotMinutes === from) continue;
        if (candidate.hardViolations.length > 0) continue;

        relocate(draft, candidate.slotMinutes);
        const next = evaluateSolution();
        if (next.feasible && next.total > current.total + IMPROVEMENT_EPSILON) {
          current = next;
          improved = true;
          break;
        }
        relocate(draft, from);
      }
    }

    return { current, improved };
  }

  /**
   * Try exchanging the slots of every pair of movable lunches.
   *
   * Swaps catch what single moves cannot: two people who would each be better
   * off in the other's slot but where moving either one alone is a step
   * backwards, which is exactly the "she should have had 12:00 and he 13:00"
   * case managers notice first.
   */
  function trySwaps(startingPoint) {
    let current = startingPoint;
    let improved = false;
    const ids = movableIds();

    for (let i = 0; i < ids.length; i += 1) {
      if (budget <= 0) break;
      for (let j = i + 1; j < ids.length; j += 1) {
        if (budget <= 0) break;

        const first = drafts.get(ids[i]);
        const second = drafts.get(ids[j]);
        const firstEntry = searchEntries.get(ids[i]);
        const secondEntry = searchEntries.get(ids[j]);
        if (!first || !second || !firstEntry || !secondEntry) continue;
        if (first.slotMinutes === second.slotMinutes) continue;

        const slotA = first.slotMinutes;
        const slotB = second.slotMinutes;
        // Neither may be pushed into a slot that is not even on their list.
        if (!isUsableSlot(firstEntry, slotB) || !isUsableSlot(secondEntry, slotA)) continue;

        relocate(first, slotB);
        relocate(second, slotA);
        const next = evaluateSolution();
        if (next.feasible && next.total > current.total + IMPROVEMENT_EPSILON) {
          current = next;
          improved = true;
          continue;
        }
        relocate(first, slotA);
        relocate(second, slotB);
      }
    }

    return { current, improved };
  }

  /**
   * Slide a complete compact group one slot earlier or later as a block.
   *
   * Single-person moves cannot translate a gap-free run: moving the first
   * person temporarily opens a hole and is correctly rejected, even when the
   * whole run would be better half an hour later. Moving the group atomically
   * preserves its shape while letting preferred times choose its best position.
   */
  function tryGroupShifts(startingPoint) {
    let current = startingPoint;
    let improved = false;
    const groups = [...new Set(state.assigned.map((draft) => draft.group ?? 'day'))].sort();

    for (const group of groups) {
      if (budget <= 0) break;
      const allInGroup = state.assigned.filter((draft) => (draft.group ?? 'day') === group);
      const members = allInGroup.filter((draft) => entryById.has(draft.employeeId));
      // A locked lunch anchors its run; sliding only the unlocked members would
      // no longer be an atomic translation and could manufacture a new gap.
      if (members.length === 0 || members.length !== allInGroup.length) continue;

      for (const delta of [-slotInterval, slotInterval]) {
        if (budget <= 0) break;
        const usable = members.every((draft) => {
          const entry = searchEntries.get(draft.employeeId);
          return entry && isUsableSlot(entry, draft.slotMinutes + delta);
        });
        if (!usable) continue;

        const original = members.map((draft) => [draft, draft.slotMinutes]);
        for (const [draft, from] of original) relocate(draft, from + delta);
        const next = evaluateSolution();
        if (next.feasible && next.total > current.total + IMPROVEMENT_EPSILON) {
          current = next;
          improved = true;
          break;
        }
        for (const [draft, from] of original) relocate(draft, from);
      }
    }

    return { current, improved };
  }

  function isUsableSlot(entry, slotMinutes) {
    const candidate = candidateAt(entry, slotMinutes);
    return Boolean(candidate) && candidate.hardViolations.length === 0;
  }

  /* ======================================================================== */
  /* 6. Soft-rule relaxation                                                  */
  /* ======================================================================== */

  /**
   * Retry one entry with the two soft rules switched off.
   *
   * The relaxed copy widens the *search* to the whole shift while keeping the
   * original window on the entry, so the window still costs score even though it
   * no longer blocks: a lunch pushed outside the window should look wrong in the
   * explain panel, not be invisible. The settling-in guard rails, the shift
   * itself, the staffing floor and the simultaneous cap are copied across
   * untouched - none of those is negotiable.
   */
  function relaxAndPlace(entry, records) {
    const shift = entry?.shift?.kind === 'shift' ? entry.shift : null;
    if (!shift) return false;

    const afterStart = Number.isFinite(entry.rule?.minMinutesAfterShiftStart)
      ? entry.rule.minMinutesAfterShiftStart
      : numberOr(dayConfig.minMinutesAfterShiftStart, 0);
    const beforeEnd = Number.isFinite(entry.rule?.minMinutesBeforeShiftEnd)
      ? entry.rule.minMinutesBeforeShiftEnd
      : numberOr(dayConfig.minMinutesBeforeShiftEnd, 0);

    const relaxedEntry = {
      ...entry,
      earliestMinutes: numberOr(entry.hardEarliestMinutes, shift.startMinutes + afterStart),
      latestMinutes: numberOr(entry.hardLatestMinutes, shift.endMinutes - lunchMinutes - beforeEnd),
      relaxations: [RELAX_WINDOW, RELAX_SPREAD],
    };

    entryById.set(entry.employeeId, relaxedEntry);
    searchEntries.set(entry.employeeId, relaxedEntry);

    const best = bestFeasible(relaxedEntry);
    if (!best) {
      entryById.set(entry.employeeId, entry);
      searchEntries.set(entry.employeeId, entry);
      return false;
    }

    place(relaxedEntry, best.candidate.slotMinutes, best.breakdown);
    recordRelaxations(entry, best.candidate.slotMinutes, records);
    return true;
  }

  /** Record only the rules that this particular placement actually gave up. */
  function recordRelaxations(entry, slotMinutes, records) {
    const window = entry.window ?? { start: slotMinutes, end: slotMinutes };
    const at = formatMinutes(slotMinutes);

    if (slotMinutes < window.start || slotMinutes > window.end) {
      const outsideBy = slotMinutes < window.start ? window.start - slotMinutes : slotMinutes - window.end;
      records.push({
        employeeId: entry.employeeId,
        rule: RELAX_WINDOW,
        detail: `${entry.name} was placed at ${at}, ${formatDuration(outsideBy)} outside the `
          + `${formatMinutes(window.start)}-${formatMinutes(window.end)} lunch window, because no slot `
          + `inside it was free without breaking cover on ${dayLabel()}.`,
      });
    }

    const spread = numberOr(dayConfig.preferredSpreadMinutes, 0);
    if (spread > 0) {
      const nearest = state.assigned
        .filter((draft) => draft.employeeId !== entry.employeeId)
        .reduce((best, draft) => Math.min(best, Math.abs(draft.slotMinutes - slotMinutes)), Infinity);
      if (Number.isFinite(nearest) && nearest < spread) {
        records.push({
          employeeId: entry.employeeId,
          rule: RELAX_SPREAD,
          detail: `${entry.name}'s lunch at ${at} is only ${formatDuration(nearest)} from the nearest `
            + `other break, against the ${formatDuration(spread)} spacing ${dayLabel()} asks for.`,
        });
      }
    }
  }

  /* ======================================================================== */
  /* Results                                                                  */
  /* ======================================================================== */

  /**
   * Turn the final drafts into Assignments, explaining each one against the
   * arrangement that actually shipped rather than the one it was placed into.
   * Explanations built mid-search would quote occupancy that no longer exists.
   */
  function buildAssignments() {
    return movableIds().map((employeeId) => {
      const entry = searchEntries.get(employeeId);
      const draft = drafts.get(employeeId);
      const scored = scoreAllDetached(entry);
      const chosen = scored.find((item) => item.candidate.slotMinutes === draft.slotMinutes);
      const breakdown = chosen?.breakdown ?? null;

      const assignment = createAssignment({
        employeeId,
        name: entry.name,
        dayKey: entry.dayKey ?? dayKey,
        slotMinutes: draft.slotMinutes,
        durationMinutes: lunchMinutes,
        group: entry.group,
        locked: false,
        manual: false,
        source: ASSIGNMENT_SOURCES.AUTO,
        score: Number.isFinite(breakdown?.total) ? breakdown.total : null,
      });

      assignment.explanation = explainAssignment({
        entry,
        assignment,
        breakdown,
        alternatives: scored,
        context: ctx,
      });
      return assignment;
    });
  }

  /**
   * Describe why somebody eligible got nothing.
   *
   * The blockers are gathered from every slot that was looked at, de-duplicated
   * in the order they were first hit, so the manager sees the real reasons in
   * the order the solver met them rather than a generic "no valid slot".
   */
  function describeFailure(entry) {
    const scored = scoreAllDetached(entry);
    const blockers = [];
    for (const item of scored) {
      for (const blocker of item.breakdown.blockers) {
        if (!blockers.includes(blocker)) blockers.push(blocker);
      }
    }

    const span = scored.length > 0
      ? `${formatMinutes(scored[0].candidate.slotMinutes)} and `
        + `${formatMinutes(scored[scored.length - 1].candidate.slotMinutes)}`
      : 'the lunch window';

    return {
      employeeId: entry.employeeId,
      name: entry.name,
      reason: INELIGIBLE.NO_VALID_SLOT,
      reasonText: scored.length === 0
        ? `${entry.name} is owed a lunch on ${dayLabel()} but the guard rails leave no slot at all to `
          + 'put one in - this needs a manual override.'
        : `${entry.name} is owed a lunch on ${dayLabel()} but all ${scored.length} slots between `
          + `${span} break a hard rule. Nearest problem: ${blockers[0] ?? 'no slot was usable'}.`,
      consideredSlots: scored.length,
      blockers,
    };
  }

  function dayLabel() {
    return DAY_LABELS[dayKey] ?? 'this day';
  }
}

/* -------------------------------------------------------------------------- */
/* Ordering and coercion                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The queue order, and therefore who gets first pick.
 *
 * Group first so the day list is settled before the evening one; then the ideal
 * lunch time; then the shift start, which is the rule the manual schedules
 * follow - where two people want the same time the earlier starter wins it and
 * the other moves on. Two people who started together are separated by who
 * finishes first: on a Sunday evening a 16:00-23:00 and a 16:00-00:00 both want
 * 19:00, and the one with an hour less left to work is the one who should eat
 * first. Name and id only ever break a genuine dead heat, and exist purely so
 * the result cannot depend on rota row order.
 */
function compareEntries(a, b) {
  const byGroup = (GROUP_ORDER[a?.group] ?? 9) - (GROUP_ORDER[b?.group] ?? 9);
  if (byGroup !== 0) return byGroup;

  const byPreferred = numberOr(a?.preferredMinutes, 0) - numberOr(b?.preferredMinutes, 0);
  if (byPreferred !== 0) return byPreferred;

  const byStart = numberOr(a?.shift?.startMinutes, 0) - numberOr(b?.shift?.startMinutes, 0);
  if (byStart !== 0) return byStart;

  const byEnd = finishRank(a) - finishRank(b);
  if (byEnd !== 0) return byEnd;

  const byName = String(a?.name ?? '').localeCompare(String(b?.name ?? ''), 'en-GB');
  if (byName !== 0) return byName;

  return compareIds(a?.employeeId, b?.employeeId);
}

/**
 * When a shift ends, on the same axis as its start so an overnight end past
 * midnight still reads as later. A row with no end falls back to its own start,
 * which keeps a missing value neutral against same-start colleagues rather than
 * sorting it to the front.
 */
function finishRank(entry) {
  const start = numberOr(entry?.shift?.startMinutes, 0);
  return numberOr(entry?.shift?.endMinutes, start);
}

/** Output order: by time, then by name, so exports read down the clock. */
function compareAssignments(a, b) {
  const bySlot = numberOr(a?.slotMinutes, 0) - numberOr(b?.slotMinutes, 0);
  if (bySlot !== 0) return bySlot;
  const byName = String(a?.name ?? '').localeCompare(String(b?.name ?? ''), 'en-GB');
  return byName !== 0 ? byName : compareIds(a?.employeeId, b?.employeeId);
}

function compareIds(a, b) {
  return String(a ?? '').localeCompare(String(b ?? ''), 'en-GB');
}

/** Locked assignments carrying a real slot - anything else cannot be honoured. */
function readLocked(context) {
  const source = Array.isArray(context?.lockedAssignments) ? context.lockedAssignments : [];
  return source
    .filter((assignment) => assignment?.employeeId && Number.isFinite(assignment.slotMinutes))
    .slice()
    .sort(compareAssignments);
}

/**
 * Accept the fairness carry-over as a Map, entry pairs or a plain object.
 * Object keys are sorted before insertion so the resulting Map iterates in the
 * same order whatever the JSON that produced it looked like.
 */
function seedFairness(source) {
  const map = new Map();
  if (source instanceof Map) {
    for (const key of [...source.keys()].sort(compareIds)) {
      map.set(key, numberOr(source.get(key), 0));
    }
  } else if (Array.isArray(source)) {
    for (const [key, value] of source) map.set(key, numberOr(value, 0));
  } else if (source && typeof source === 'object') {
    for (const key of Object.keys(source).sort(compareIds)) map.set(key, numberOr(source[key], 0));
  }
  return map;
}

function numberOr(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function positive(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}
